/**
 * "Resumo por empresa": pronto para centenas de empresas -- busca, ordenacao
 * por coluna e "ver mais", nunca a lista inteira de uma vez.
 *
 * Decisoes nao obvias:
 *  1. Percentual, modelo de cobranca e politica de estorno vem do cadastro
 *     VIGENTE (`useCompany().companies`): a administracao pode ter mudado
 *     agora em Empresas -> Comissao e cobranca. Quando o vigente difere do
 *     percentual congelado nas vendas do periodo, a celula mostra os dois e o
 *     selo "alterado" -- a comissao apurada nao muda (ADR-001).
 *  2. "Comercial" e "Estornos" sao colunas proprias: quantas vendas a
 *     vendedora fechou (E5) e quantos estornos chegaram x quanto reverteu.
 *  3. A fatura do mes e retrato de AGORA (mes corrente), independente da
 *     janela do cabecalho; o cabecalho da coluna diz o mes.
 *  4. O rodape soma o que esta FILTRADO pela busca (todas as linhas, nao so
 *     as visiveis) e o percentual fica vazio nele: media de percentuais de
 *     empresas diferentes nao significa nada.
 *  5. Dez colunas: abaixo de `md` vira lista de cartoes; acima, rola em
 *     `overflow-x-auto` com o cabecalho visivel.
 */

import { useId, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Badge, Button, Icon, IconButton, Input, LinkButton, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps, VALOR_AUSENTE } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import { abaComissaoDaEmpresa } from '../insights';
import { normalizar } from '../regras';
import type { CompanyCommissionSummary } from '../types';
import { MODELO_COBRANCA, POLITICA_ESTORNO, STATUS_FATURA, formatMes, plural } from '../vocabulario';
import { Delta } from './Delta';
import { IdentidadeEmpresa, identidadeDaEmpresa } from './IdentidadeEmpresa';

const LOTE = 10;

type Coluna = 'empresa' | 'conversoes' | 'recuperado' | 'comissao' | 'fee' | 'comercial' | 'estornos';

const COLUNAS: Readonly<Record<Coluna, { readonly rotulo: string; readonly numerica: boolean; readonly titulo: string }>> = {
  empresa: { rotulo: 'Empresa', numerica: false, titulo: 'Nome da empresa' },
  conversoes: { rotulo: 'Conversões', numerica: true, titulo: 'Vendas recuperadas ativas no período; as revertidas aparecem abaixo' },
  recuperado: { rotulo: 'Recuperado', numerica: true, titulo: 'Valor pago pelos leads recuperados (bruto)' },
  comissao: { rotulo: 'Comissão', numerica: true, titulo: 'Comissão ativa do Converte Fácil e a variação contra o período anterior' },
  fee: { rotulo: '% vigente', numerica: true, titulo: 'Percentual do cadastro hoje. Vendas já apuradas ficam com o percentual da época' },
  comercial: { rotulo: 'Comercial', numerica: true, titulo: 'Vendas fechadas pela vendedora depois de um toque da régua (ADR-001 E5)' },
  estornos: { rotulo: 'Estornos', numerica: true, titulo: 'Estornos recebidos e quanto de comissão a política reverteu' },
};

const ORDEM: readonly Coluna[] = ['empresa', 'conversoes', 'recuperado', 'comissao', 'fee', 'comercial', 'estornos'];

interface Linha {
  readonly row: CompanyCommissionSummary;
  readonly feeVigente: number;
  readonly empresa: CompanySummary | undefined;
}

function valorDa(l: Linha, coluna: Coluna): number {
  switch (coluna) {
    case 'conversoes':
      return l.row.conversionCount;
    case 'recuperado':
      return l.row.recoveredAmountCents;
    case 'comissao':
      return l.row.commissionCents;
    case 'fee':
      return l.feeVigente;
    case 'comercial':
      return l.row.comercialCount;
    case 'estornos':
      return l.row.refundCount;
    case 'empresa':
      return 0;
  }
}

function PercentualVigente({ l }: { readonly l: Linha }): JSX.Element {
  const congelados = l.row.feeBpsInPeriod;
  const alterado = congelados.length > 0 && !congelados.every((f) => f === l.feeVigente);
  return (
    <span className="flex flex-col items-end gap-0.5">
      <span className="tabular-nums text-ink">{formatPercentFromBps(l.feeVigente)}</span>
      {alterado ? (
        <>
          <Badge tone="info">alterado</Badge>
          <span className="text-[11px] text-ink-subtle">no período: {congelados.map(formatPercentFromBps).join(', ')}</span>
        </>
      ) : null}
    </span>
  );
}

function Cobranca({ l }: { readonly l: Linha }): JSX.Element {
  const modelo = MODELO_COBRANCA[l.empresa?.billingModel ?? l.row.billingModel];
  const politica = POLITICA_ESTORNO[l.empresa?.refundPolicy ?? l.row.refundPolicy];
  return (
    <span className="flex flex-col items-start gap-1">
      <Badge tone={modelo.tone}>{modelo.curto}</Badge>
      <span className="text-[11px] text-ink-subtle" title={politica.explica}>
        Estorno: {politica.curto}
      </span>
    </span>
  );
}

function Estornos({ row, alinhar = 'end' }: { readonly row: CompanyCommissionSummary; readonly alinhar?: 'start' | 'end' }): JSX.Element {
  if (row.refundCount === 0) {
    return <span className="text-ink-subtle">{VALOR_AUSENTE}</span>;
  }
  return (
    <span className={`flex flex-col ${alinhar === 'end' ? 'items-end' : 'items-start'}`}>
      <span className="tabular-nums text-ink">{formatCount(row.refundCount)} recebidos</span>
      <span className={`text-[11px] tabular-nums ${row.reversedCommissionCents > 0 ? 'text-status-critical' : 'text-ink-subtle'}`}>
        {row.reversedCount === 0 ? 'nenhum reverteu' : `${formatBRLFromCents(row.reversedCommissionCents)} revertidos`}
      </span>
    </span>
  );
}

function Comercial({ row, alinhar = 'end' }: { readonly row: CompanyCommissionSummary; readonly alinhar?: 'start' | 'end' }): JSX.Element {
  if (row.comercialCount === 0) {
    return <span className="text-ink-subtle">{VALOR_AUSENTE}</span>;
  }
  return (
    <span className={`flex flex-col ${alinhar === 'end' ? 'items-end' : 'items-start'}`}>
      <span className="tabular-nums text-ink">
        {formatCount(row.comercialCount)} {plural(row.comercialCount, 'venda', 'vendas')}
      </span>
      <span className="text-[11px] tabular-nums text-ink-subtle">{formatBRLFromCents(row.comercialAmountCents)}</span>
    </span>
  );
}

export function ResumoPorEmpresa({
  rows,
  companies,
  empresaFiltrada,
  onFiltrar,
}: {
  readonly rows: readonly CompanyCommissionSummary[];
  /** Cadastro vigente (useCompany). */
  readonly companies: readonly CompanySummary[];
  readonly empresaFiltrada: string | null;
  readonly onFiltrar: (companyId: string | null) => void;
}): JSX.Element {
  const idBusca = useId();
  const location = useLocation();
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<{ readonly coluna: Coluna; readonly desc: boolean }>({ coluna: 'comissao', desc: true });
  const [visiveis, setVisiveis] = useState(LOTE);

  const linhas = useMemo<readonly Linha[]>(
    () =>
      rows.map((row) => {
        const empresa = companies.find((c) => c.id === row.companyId);
        return { row, empresa, feeVigente: empresa?.feeBps ?? row.feeBps };
      }),
    [rows, companies],
  );

  const filtradas = useMemo(() => {
    const termo = normalizar(busca);
    const lista = termo === '' ? linhas : linhas.filter((l) => normalizar(l.row.companyName).includes(termo));
    const sinal = ordem.desc ? -1 : 1;
    return [...lista].sort((a, b) => {
      if (ordem.coluna === 'empresa') {
        return sinal * a.row.companyName.localeCompare(b.row.companyName, 'pt-BR');
      }
      const diff = valorDa(a, ordem.coluna) - valorDa(b, ordem.coluna);
      return diff === 0 ? a.row.companyName.localeCompare(b.row.companyName, 'pt-BR') : sinal * diff;
    });
  }, [linhas, busca, ordem]);

  const pagina = filtradas.slice(0, visiveis);
  const totais = filtradas.reduce(
    (acc, l) => ({
      conversoes: acc.conversoes + l.row.conversionCount,
      recuperado: acc.recuperado + l.row.recoveredAmountCents,
      comissao: acc.comissao + l.row.commissionCents,
      comercial: acc.comercial + l.row.comercialCount,
      estornos: acc.estornos + l.row.refundCount,
    }),
    { conversoes: 0, recuperado: 0, comissao: 0, comercial: 0, estornos: 0 },
  );
  const mesDaFatura = rows[0]?.invoice.month;
  const tituloFatura = mesDaFatura === undefined ? 'Fatura do mês' : `Fatura de ${formatMes(mesDaFatura)}`;

  const ordenar = (coluna: Coluna): void => {
    setOrdem((atual) => (atual.coluna === coluna ? { coluna, desc: !atual.desc } : { coluna, desc: coluna !== 'empresa' }));
    setVisiveis(LOTE);
  };

  // Funcoes de renderizacao, nao componentes: um componente declarado aqui
  // dentro teria identidade nova a cada render e o botao perderia o foco ao ordenar.
  const cabecalho = (coluna: Coluna): JSX.Element => {
    const info = COLUNAS[coluna];
    const ativa = ordem.coluna === coluna;
    return (
      <th key={coluna} scope="col" aria-sort={ativa ? (ordem.desc ? 'descending' : 'ascending') : 'none'} className={`px-3 py-3 font-medium first:pl-5 ${info.numerica ? 'text-right' : ''}`}>
        <button
          type="button"
          onClick={() => ordenar(coluna)}
          title={info.titulo}
          className={`inline-flex items-center gap-1 rounded-md uppercase tracking-wide transition-colors hover:text-ink ${ativa ? 'text-ink' : ''} ${info.numerica ? 'flex-row-reverse' : ''}`}
        >
          {info.rotulo}
          <span aria-hidden="true" className={`text-[10px] ${ativa ? '' : 'opacity-40'}`}>
            {ativa ? (ordem.desc ? '▼' : '▲') : '↕'}
          </span>
        </button>
      </th>
    );
  };

  const acoes = (l: Linha): JSX.Element => {
    const nome = l.row.companyName;
    const filtrando = empresaFiltrada === l.row.companyId;
    return (
      <div className="flex items-center gap-1">
        <IconButton
          icon={filtrando ? 'x' : 'filtro'}
          label={filtrando ? `Tirar o filtro de ${nome}` : `Filtrar a página por ${nome}`}
          size="sm"
          onClick={() => onFiltrar(filtrando ? null : l.row.companyId)}
        />
        <LinkButton to={abaComissaoDaEmpresa(l.row.companyId, location.search)} keepSearch={false} variant="ghost" size="sm" icon="empresa">
          Cadastro<span className="sr-only"> de {nome}, aba Comissão e cobrança</span>
        </LinkButton>
      </div>
    );
  };

  return (
    <SectionCard
      id="resumo-por-empresa"
      title="Resumo por empresa"
      icon="empresa"
      className="scroll-mt-20"
      description="O que cada empresa recuperou no período, a comissão, o percentual vigente, as vendas do comercial, os estornos, como é cobrada e a fatura do mês corrente."
      padded={false}
      aside={
        <>
          <Badge tone="neutral">
            {formatCount(rows.length)} {plural(rows.length, 'empresa', 'empresas')}
          </Badge>
          <div className="relative">
            <label htmlFor={idBusca} className="sr-only">
              Buscar empresa pelo nome
            </label>
            <Icon name="busca" size="sm" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
            <Input
              id={idBusca}
              size="sm"
              type="search"
              placeholder="Buscar empresa"
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setVisiveis(LOTE);
              }}
              className="w-44 pl-9"
            />
          </div>
        </>
      }
    >
      {rows.length === 0 ? (
        <div className="flex flex-col items-start gap-3 px-5 pb-5">
          <p className="text-sm text-ink-subtle">Nenhuma empresa no recorte. Cada empresa ativa aparece aqui assim que a primeira venda recuperada for paga.</p>
          <LinkButton to="/admin/empresas/nova" variant="secondary" size="sm" icon="mais">
            Nova empresa
          </LinkButton>
        </div>
      ) : filtradas.length === 0 ? (
        <div className="flex flex-col items-start gap-3 px-5 pb-5">
          <p className="text-sm text-ink-subtle">Nenhuma empresa com «{busca.trim()}» no nome.</p>
          <Button variant="ghost" size="sm" icon="x" onClick={() => setBusca('')}>
            Limpar busca
          </Button>
        </div>
      ) : (
        <>
          {/* Desktop */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[78rem] border-collapse text-sm">
              <caption className="sr-only">
                Empresas com conversões, recuperado, comissão, percentual vigente, vendas do comercial, estornos, cobrança e fatura do mês corrente. Ordenável pelos
                botões do cabeçalho.
              </caption>
              <thead>
                <tr className="border-y border-line text-left text-xs uppercase tracking-wide text-ink-subtle">
                  {ORDEM.map((coluna) => cabecalho(coluna))}
                  <th scope="col" className="px-3 py-3 font-medium" title="Modelo de cobrança e política de estorno no cadastro hoje">
                    Cobrança
                  </th>
                  <th scope="col" className="px-3 py-3 font-medium">
                    {tituloFatura}
                  </th>
                  <th scope="col" className="px-5 py-3 font-medium">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pagina.map((l) => {
                  const fatura = STATUS_FATURA[l.row.invoice.status];
                  const destaque = empresaFiltrada === l.row.companyId;
                  return (
                    <tr key={l.row.companyId} className={`border-b border-line/60 align-top transition-colors last:border-0 hover:bg-ink/5 focus-within:bg-ink/5 ${destaque ? 'bg-brand/5' : ''}`}>
                      <th scope="row" className="px-5 py-3 text-left font-normal">
                        <IdentidadeEmpresa empresa={identidadeDaEmpresa(l.row.companyId, companies, l.row.companyName)} size="sm" />
                      </th>
                      <td className="px-3 py-3 text-right tabular-nums text-ink">
                        {formatCount(l.row.conversionCount)}
                        {l.row.reversedCount > 0 ? (
                          <span className="block text-[11px] text-status-critical">
                            {formatCount(l.row.reversedCount)} {plural(l.row.reversedCount, 'revertida', 'revertidas')}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-ink">{formatBRLFromCents(l.row.recoveredAmountCents)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        <span className="block font-medium text-ink">{formatBRLFromCents(l.row.commissionCents)}</span>
                        <Delta atual={l.row.commissionCents} anterior={l.row.previousCommissionCents} tipo="dinheiro" compacto />
                      </td>
                      <td className="px-3 py-3 text-right">
                        <PercentualVigente l={l} />
                      </td>
                      <td className="px-3 py-3 text-right">
                        <Comercial row={l.row} />
                      </td>
                      <td className="px-3 py-3 text-right">
                        <Estornos row={l.row} />
                      </td>
                      <td className="px-3 py-3">
                        <Cobranca l={l} />
                      </td>
                      <td className="px-3 py-3">
                        <Badge tone={fatura.tone} dot>
                          {fatura.label}
                        </Badge>
                        {l.row.pendingCommissionCents > 0 ? (
                          <span className="mt-1 block text-[11px] tabular-nums text-ink-subtle">{formatBRLFromCents(l.row.pendingCommissionCents)} a faturar</span>
                        ) : null}
                      </td>
                      <td className="px-5 py-3">
                        {acoes(l)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {filtradas.length > 1 ? (
                <tfoot>
                  <tr className="border-t border-line bg-elevated/60 text-ink">
                    <th scope="row" className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                      Total {busca.trim() === '' ? '' : 'da busca'}
                    </th>
                    <td className="px-3 py-3 text-right font-medium tabular-nums">{formatCount(totais.conversoes)}</td>
                    <td className="px-3 py-3 text-right font-medium tabular-nums">{formatBRLFromCents(totais.recuperado)}</td>
                    <td className="px-3 py-3 text-right font-semibold tabular-nums">{formatBRLFromCents(totais.comissao)}</td>
                    <td className="px-3 py-3 text-right text-ink-subtle">{VALOR_AUSENTE}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{formatCount(totais.comercial)}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{formatCount(totais.estornos)}</td>
                    <td className="px-3 py-3" />
                    <td className="px-3 py-3" />
                    <td className="px-5 py-3" />
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>

          {/* Mobile */}
          <ul className="flex flex-col gap-3 px-4 pb-4 md:hidden">
            {pagina.map((l) => {
              const fatura = STATUS_FATURA[l.row.invoice.status];
              return (
                <li key={l.row.companyId} className="flex flex-col gap-3 rounded-xl border border-line bg-surface px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <IdentidadeEmpresa empresa={identidadeDaEmpresa(l.row.companyId, companies, l.row.companyName)} size="md" />
                    <Cobranca l={l} />
                  </div>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div>
                      <dt className="text-xs text-ink-subtle">Comissão · {formatPercentFromBps(l.feeVigente)} vigente</dt>
                      <dd className="font-medium tabular-nums text-ink">{formatBRLFromCents(l.row.commissionCents)}</dd>
                      <dd>
                        <Delta atual={l.row.commissionCents} anterior={l.row.previousCommissionCents} tipo="dinheiro" compacto />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-ink-subtle">Recuperado</dt>
                      <dd className="tabular-nums text-ink">{formatBRLFromCents(l.row.recoveredAmountCents)}</dd>
                      <dd className="text-[11px] text-ink-subtle">
                        {formatCount(l.row.conversionCount)} {plural(l.row.conversionCount, 'conversão', 'conversões')}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-ink-subtle">Comercial</dt>
                      <dd>
                        <Comercial row={l.row} alinhar="start" />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-ink-subtle">Estornos</dt>
                      <dd>
                        <Estornos row={l.row} alinhar="start" />
                      </dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-xs text-ink-subtle">{tituloFatura}</dt>
                      <dd className="mt-0.5">
                        <Badge tone={fatura.tone} dot>
                          {fatura.label}
                        </Badge>
                      </dd>
                    </div>
                  </dl>
                  <div className="flex justify-end">{acoes(l)}</div>
                </li>
              );
            })}
          </ul>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-xs text-ink-muted">
            <span>
              Mostrando {formatCount(pagina.length)} de {formatCount(filtradas.length)} {plural(filtradas.length, 'empresa', 'empresas')}
            </span>
            {filtradas.length > pagina.length ? (
              <Button variant="secondary" size="sm" icon="chevron" onClick={() => setVisiveis((v) => v + LOTE)}>
                Ver mais {formatCount(Math.min(LOTE, filtradas.length - pagina.length))}
              </Button>
            ) : null}
          </div>
        </>
      )}
    </SectionCard>
  );
}
